import {inject, Injectable} from '@angular/core';
import {StateManagerService} from '@core/services';
import {StateManager} from '@core/types';
import {AuthState, User} from '@auth/types';
import {CONFIG, STORAGE_KEYS} from '@auth/utils';
import {log} from '@core/utils';
import {Router} from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {

  private stateManager = inject(StateManagerService);
  private router = inject(Router);

  private state: StateManager<AuthState> = this.stateManager.createState<AuthState>({
    storage: 'sessionStorage',
    key: STORAGE_KEYS.AUTH,
    storageOptions: {
      ttl: CONFIG.TTL_AUTH,
      prefix: 'app',
      encrypt: true
    },
    initialValue: {
      user: null,
      token: null,
      isLoading: false,
      lastLoginTime: null
    }
  });

  user = this.state.computed(state => state.user);
  token = this.state.computed(state => state.token);
  isLoading = this.state.computed(state => state.isLoading);
  isAuthenticated = this.state.computed(state => state.user !== null && state.token !== null);
  isAdmin = this.state.computed(state => state.user?.roles?.includes('ROLE_ADMIN'));

  async initialize() {
    await this.state.loadFromStorage();
    log.debug('Auth state initialized');
  }

  login(token: string) {
    try {

      const payload = this.decodeJwtPayload(token);

      const user: User = {
        id: payload.id,
        email: payload.email,
        firstName: payload.firstName || payload.email,
        lastName: payload.lastName || '',
        memberships: Array.isArray(payload.memberships)
          ? payload.memberships
          : [],
        roles: payload.roles || [],
      }

      log.debug('JWT memberships:', user.memberships);

      this.state.update(current => ({
        ...current,
        user,
        token,
        isLoading: false,
        lastLoginTime: Date.now(),
      }));
    } catch (error) {
      throw new Error('Error al procesar el token JWT');
    }
  }

  logout() {
    this.state.clearStorage();
    this.state.set({
      user: null,
      token: null,
      isLoading: false,
      lastLoginTime: null
    });
    this.stateManager.clearAllStorage(['localStorage']);
    this.router.navigate(['/']);
  }

  setLoading(isLoading: boolean) {
    this.state.update(current => ({
      ...current,
      isLoading,
    }))
  }

  updateUser(user: Partial<User>) {
    this.state.update(current => ({
      ...current,
      user: current.user ? {...current.user, ...user} : null,
    }))
  }

  private decodeJwtPayload(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decodedPayload = atob(payload);
      return JSON.parse(decodedPayload);
    } catch (error) {
      throw new Error('Token JWT inválido');
    }
  }
}
